import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { seoCompetitorAction, seoCompetitorState } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Pill, Loading, Empty, DataTable } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/competitors")({
  head: () => ({ meta: [{ title: "Competitors · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CompetitorsPage,
});

function CompetitorsPage() {
  const load = useServerFn(seoCompetitorState);
  const act = useServerFn(seoCompetitorAction);
  const q = useQuery({ queryKey: ["seo-competitors"], queryFn: () => load() });
  const mutate = useMutation({
    mutationFn: (action: "seed" | "crawl") => act({ data: { action } }),
    onSuccess: () => {
      toast.success("Done");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const d = q.data as any;

  return (
    <SeoPage
      title="Competitor watch"
      description="Tracks structural changes on competing investor-lending sites — new tools, new sections, new structured data — as evidence of gaps on our side. Their wording is never copied."
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => mutate.mutate("seed")} disabled={mutate.isPending}>
            Add default competitors
          </Button>
          <Button size="sm" onClick={() => mutate.mutate("crawl")} disabled={mutate.isPending}>
            {mutate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Check next batch
          </Button>
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <Panel title="Watch list">
            {d?.competitors?.length ? (
              <DataTable
                head={["Competitor", "Last checked", "Status"]}
                rows={d.competitors.map((c: any) => [
                  `${c.label} (${c.domain})`,
                  c.last_crawled_at ? new Date(c.last_crawled_at).toLocaleString() : "Never",
                  c.last_error ? <Pill tone="bad">{c.last_error}</Pill> : <Pill tone="good">OK</Pill>,
                ])}
              />
            ) : (
              <Empty>No competitors yet — add the default list to start.</Empty>
            )}
          </Panel>

          <Panel title="Recent changes spotted">
            {d?.diffs?.length ? (
              <DataTable
                head={["When", "What changed", "Impact", "What it means for us"]}
                rows={d.diffs.map((f: any) => [
                  new Date(f.created_at).toLocaleDateString(),
                  f.kind.replace(/_/g, " "),
                  <Pill tone={f.severity === "high" ? "bad" : f.severity === "medium" ? "warn" : "neutral"}>{f.severity}</Pill>,
                  f.suggestion,
                ])}
              />
            ) : (
              <Empty>Nothing yet. The first check records a baseline; changes appear from the second check onward.</Empty>
            )}
          </Panel>

          <Panel title="Alerts">
            {d?.alerts?.length ? (
              <DataTable
                head={["Last seen", "Alert", "Times", "Detail"]}
                rows={d.alerts.map((a: any) => [
                  new Date(a.last_seen_at).toLocaleDateString(),
                  a.title,
                  a.occurrences,
                  a.detail,
                ])}
              />
            ) : (
              <Empty>No open alerts.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
